import type { AccountInfo, EventMessage } from "@azure/msal-browser";
import { EventType, PublicClientApplication } from "@azure/msal-browser";
import type { PostHog } from "posthog-js";
import { config } from "~initializer/config.ts";
import { createE2eFakeMsalInstance, getE2eAuth } from "./e2eFakeMsal.ts";
import { msalLoggerOptions } from "./msalLoggerOptions.ts";

export async function initAuth(postHogClient: PostHog) {
  // In the e2e environment, skip Azure and fake the logged-in user when the
  // test runner has injected auth. Inert in every real deployment.
  const e2eAuth = getE2eAuth();
  if (e2eAuth) {
    return createE2eFakeMsalInstance(e2eAuth, postHogClient);
  }

  const msalInstance = new PublicClientApplication({
    auth: {
      clientId: config().msalClientId ?? "",
      authority: config().msalAuthority ?? "",
      redirectUri: config().msalRedirectUri ?? "",
      postLogoutRedirectUri: config().msalRedirectUri ?? "",
    },
    cache: {
      cacheLocation: "localStorage",
    },
    system: {
      loggerOptions: msalLoggerOptions(config().msalLogLevel),
    },
  });

  await msalInstance.initialize();

  // Check if there are already accounts in the browser session
  // If so, set the first account as the active account
  const accounts = msalInstance.getAllAccounts();
  if (!msalInstance.getActiveAccount() && accounts && accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      // Set the active account - this simplifies token acquisition.
      const account = event.payload as AccountInfo;
      msalInstance.setActiveAccount(account);
      postHogClient.identify(account.username, { email: account.username });
    }
  });

  return msalInstance;
}
